import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Button, Typography } from '@mui/material';

import useDebounce from '../../../../hooks/useDebounce';

import { BackButton, PageTitle, TextArea } from '../../../../components/common';
import ButtonContainer from '../../../../components/ButtonContainer';

import { StyledVerticalContent } from './CustomerNote.styled';

const CustomerNote = ({
  backButtonLink,
  nextButtonLink,
  pageTitleId,
  maxWords,
  pageDescriptionId,
  customerNote,
  setCustomerNote,
  placeholder,
  errorMessageId,
  showNextButton = true,
  skipText = '',
  customerNoteRequired = true,
}) => {
  const history = useHistory();
  const intl = useIntl();
  const [isValid, setIsValid] = useState(true);
  const [text, setText] = useState(customerNote);
  const debouncedInfoText = useDebounce(text, 500);

  const maxLetterExceeded = useCallback(
    (t) => {
      return t.trim().split(/\s+/).length > maxWords;
    },
    [maxWords]
  );

  useEffect(() => {
    if (debouncedInfoText) {
      if (maxLetterExceeded(debouncedInfoText)) {
        setCustomerNote('');
        setIsValid(false);
      } else {
        setCustomerNote(debouncedInfoText);
        setIsValid(true);
      }
    } else {
      setCustomerNote('');
    }
  }, [debouncedInfoText, setCustomerNote, maxLetterExceeded]);

  const handleCustomerNoteChange = (ev) => {
    if (!ev) return;
    const value = ev?.target?.value;
    setText(value);
  };

  const defaultErrorMessageId = 'new_claim.date.info_text_max_error_message';

  return (
    <>
      <BackButton to={backButtonLink}>
        <Typography variant="h3">
          <FormattedMessage id="common.button.back" />
        </Typography>
      </BackButton>

      <PageTitle>
        <Typography variant="h1">
          <FormattedMessage id={pageTitleId} />
        </Typography>
      </PageTitle>

      <StyledVerticalContent>
        <Typography variant="p" color="professional_blue">
          <FormattedMessage id={pageDescriptionId} />
        </Typography>

        <TextArea
          name="treatment_Info_box"
          placeholder={intl.formatMessage({
            id: placeholder,
          })}
          value={text}
          onChange={handleCustomerNoteChange}
          data-testid="treatment-info-text"
        />

        {!isValid && (
          <Typography variant="p" color="error">
            <FormattedMessage id={errorMessageId || defaultErrorMessageId} />
          </Typography>
        )}

        {skipText && (
          <Typography variant="p" color="professional_blue">
            <FormattedMessage id={skipText} />
          </Typography>
        )}
      </StyledVerticalContent>

      {showNextButton && (
        <ButtonContainer>
          <Button
            disabled={
              customerNoteRequired && (!debouncedInfoText || maxLetterExceeded(debouncedInfoText))
            }
            onClick={() => history.push(nextButtonLink)}
          >
            <Typography variant="buttons">
              <FormattedMessage id="common.button.next" />
            </Typography>
          </Button>
        </ButtonContainer>
      )}
    </>
  );
};

export default CustomerNote;
